// context/ReCaptchaContext.js
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNotification, NotificationSeverity } from './NotificationContext';

const ReCaptchaContext = createContext(null);

export const ReCaptchaProvider = ({ children }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const observerRef = useRef(null);
  const { showNotification } = useNotification();
  const lastTokenRef = useRef(null);
  const tokenGenerationTimeRef = useRef(null);
  
  useEffect(() => {
    const setupErrorDetection = () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      
      observerRef.current = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            for (const node of mutation.addedNodes) {
              if (node.nodeType === Node.TEXT_NODE && 
                  node.textContent?.includes('No se ha podido establecer conexión con el servicio reCAPTCHA')) {
                
                if (node.parentNode) {
                  node.parentNode.removeChild(node);
                }
                
                if (!networkError) {
                  setNetworkError(true);
                  showNotification({
                    message: "Error de conexión con el servicio de verificación. Verifique su red.",
                    severity: NotificationSeverity.WARNING,
                    autoHideDuration: 6000
                  });
                }
              }
            }
          }
        }
      });
      
      observerRef.current.observe(document.body, { 
        childList: true,
        subtree: true,
        characterData: true
      });
      
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
      };
    };
    
    const cleanup = setupErrorDetection();
    return cleanup;
  }, [networkError, showNotification]);
  
  // Verificar si el token necesita regeneración
  const shouldRegenerateToken = useCallback(() => {
    if (!lastTokenRef.current || !tokenGenerationTimeRef.current) {
      return true;
    }
    
    // Token válido por máximo 2 minutos
    const tokenAge = Date.now() - tokenGenerationTimeRef.current;
    return tokenAge > 120000;
  }, []);
  
  // Generar nuevo token fresco
  const generateFreshToken = useCallback(async (action = 'default') => {
    if (!executeRecaptcha) return null;
    
    try {
      const token = await Promise.race([
        executeRecaptcha(action),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Token generation timeout')), 10000)
        )
      ]);
      
      lastTokenRef.current = token;
      tokenGenerationTimeRef.current = Date.now();
      
      return token;
    } catch (error) {
      console.error('Error generando token reCAPTCHA:', error);
      throw error;
    }
  }, [executeRecaptcha]);
  
  const executeVerification = useCallback(async (action = 'default', forceNew = false) => {
    if (!executeRecaptcha) return null;
    
    try {
      setIsLoading(true);
      setNetworkError(false);
      
      if (!navigator.onLine) {
        throw new Error('Sin conexión a internet');
      }
      
      // Generar token fresco si es necesario
      if (forceNew || shouldRegenerateToken()) {
        const token = await generateFreshToken(action);
        return token;
      }
      
      // Usar token existente si es válido
      return lastTokenRef.current;
      
    } catch (error) {
      console.error('Error en verificación reCAPTCHA:', error);
      
      if (!navigator.onLine || error.message.includes('conexión')) {
        setNetworkError(true);
        showNotification({
          message: "Error de red. Verifique su conexión a internet.",
          severity: NotificationSeverity.WARNING
        });
      } else {
        showNotification({
          message: "Error en la verificación de seguridad. Intente más tarde.",
          severity: NotificationSeverity.ERROR
        });
      }
      
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [executeRecaptcha, shouldRegenerateToken, generateFreshToken, showNotification]);
  
  // Invalidar token actual para forzar regeneración
  const invalidateToken = useCallback(() => {
    lastTokenRef.current = null;
    tokenGenerationTimeRef.current = null;
  }, []);
  
  return (
    <ReCaptchaContext.Provider value={{ 
      executeVerification, 
      isLoading, 
      networkError,
      invalidateToken,
      resetNetworkError: () => setNetworkError(false)
    }}>
      {children}
    </ReCaptchaContext.Provider>
  );
};

export const useReCaptcha = () => {
  const context = useContext(ReCaptchaContext);
  if (!context) {
    throw new Error('useReCaptcha debe usarse dentro de un ReCaptchaProvider');
  }
  return context;
};